import { useEffect, useState } from "react";
import { adminListAnalyses, adminStats, adminListLeads, adminHealth } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { Link } from "react-router-dom";
import { RefreshCw, CheckCircle2, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("knownfor_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("analyses");
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthProbing, setHealthProbing] = useState(false);
  const [batchFilter, setBatchFilter] = useState("");  // "" = all

  const loadHealth = async (tk, { force = false } = {}) => {
    setHealthProbing(true);
    try {
      const h = await adminHealth(tk, { force });
      setHealth(h);
    } catch (_e) {
      setHealth({ ok: false, dependencies: {} });
    } finally {
      setHealthProbing(false);
    }
  };

  const load = async (tk) => {
    setLoading(true);
    setError("");
    try {
      const [s, a, l] = await Promise.all([
        adminStats(tk),
        adminListAnalyses(tk),
        adminListLeads(tk),
      ]);
      setStats(s);
      setAnalyses(a.analyses);
      setLeads(l.leads);
      setAuthed(true);
      sessionStorage.setItem("knownfor_admin_token", tk);
      // Fire health in the background (cached, so cheap)
      loadHealth(tk);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unauthorized");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 py-24">
        <div className="glass rounded-2xl p-8">
          <div className="label-eyebrow mb-3">Admin console</div>
          <div className="font-serif text-2xl mb-6">Sign in</div>
          <input
            className="input-base mb-4"
            type="password"
            placeholder="Admin token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid={TESTIDS.adminTokenInput}
          />
          {error && <div className="text-sm text-[color:var(--score-low)] mb-3">{error}</div>}
          <button
            className="btn-gold w-full justify-center"
            onClick={() => load(token)}
            data-testid={TESTIDS.adminSignInBtn}
            disabled={!token || loading}
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label-eyebrow mb-1">Admin console</div>
          <h1 className="font-serif text-3xl">FieldGuide Grader Ops</h1>
        </div>
        <button
          className="btn-ghost text-sm !py-2 !px-4"
          onClick={() => load(token)}
          data-testid="admin-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* System health strip */}
      <HealthStrip
        health={health}
        probing={healthProbing}
        onRefresh={() => loadHealth(token, { force: true })}
      />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          {[
            ["Total", stats.total_analyses],
            ["Complete", stats.complete],
            ["Running", stats.running],
            ["Failed", stats.failed],
            ["Leads", stats.total_leads],
            ["High-priority", stats.high_priority_leads],
          ].map(([label, value]) => (
            <div key={label} className="glass rounded-xl p-4">
              <div className="label-eyebrow mb-1">{label}</div>
              <div className="font-serif text-3xl">{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {["analyses", "leads"].map((t) => (
          <button
            key={t}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              tab === t
                ? "bg-[color:var(--brand-sage-muted)] text-[color:var(--brand-sage-deep)] border border-[color:var(--brand-sage)]/40"
                : "text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
            }`}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t}`}
          >
            {t === "analyses" ? "Analyses" : "Leads"}
          </button>
        ))}
      </div>

      {tab === "analyses" && (
        <>
          {/* Batch filter — lets admin slice the analyses table by batch_tag
              (e.g. providence-ri-07-2026-02) so per-market drill-in is fast. */}
          {(() => {
            const tags = Array.from(
              new Set(analyses.map((a) => a.batch_tag).filter(Boolean))
            ).sort();
            if (tags.length === 0) return null;
            return (
              <div className="flex items-center gap-3 mb-3 text-sm">
                <span className="label-eyebrow">Batch filter</span>
                <select
                  className="glass rounded-md px-3 py-1.5 text-sm bg-transparent"
                  value={batchFilter}
                  onChange={(e) => setBatchFilter(e.target.value)}
                  data-testid="admin-batch-filter"
                >
                  <option value="">All ({analyses.length})</option>
                  {tags.map((t) => {
                    const count = analyses.filter((a) => a.batch_tag === t).length;
                    return (
                      <option key={t} value={t}>
                        {t} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>
            );
          })()}
          <div className="glass rounded-2xl p-4 overflow-x-auto" data-testid={TESTIDS.adminAnalysesTable}>
            <table className="evidence-table min-w-[900px]">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Property</th>
                  <th>Batch</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Band</th>
                  <th>Cached</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {analyses
                  .filter((a) => !batchFilter || a.batch_tag === batchFilter)
                  .map((a) => {
                    const reportPath = a.access_token
                      ? `/report/${a.id}?t=${a.access_token}`
                      : `/report/${a.id}`;
                    return (
                      <tr key={a.id} data-testid={`admin-analysis-${a.id}`}>
                        <td className="font-mono text-xs">
                          {new Date(a.started_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                        </td>
                        <td>
                          {a.property_snapshot?.name}
                          <div className="text-xs text-[color:var(--text-muted)]">
                            {a.property_snapshot?.city}, {a.property_snapshot?.state}
                          </div>
                        </td>
                        <td className="text-xs" style={{ color: "var(--text-muted)" }}>
                          {a.batch_tag || "—"}
                        </td>
                        <td>
                          <span
                            className={`pill ${
                              a.status === "complete"
                                ? "pill-won"
                                : a.status === "failed"
                                  ? "pill-lost"
                                  : "pill-partial"
                            }`}
                          >
                            {a.status}
                          </span>
                        </td>
                        <td className="num">{a.headline_score ?? "—"}</td>
                        <td className="text-xs">{a.score_band || "—"}</td>
                        <td className="text-xs">
                          {a.handoff_payload_cached_at ? (
                            <span
                              className="inline-flex items-center gap-1"
                              style={{ color: "var(--score-high)" }}
                              title={`Cached at ${a.handoff_payload_cached_at}`}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> ready
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-muted)" }}>—</span>
                          )}
                        </td>
                        <td>
                          <Link
                            to={reportPath}
                            className="text-xs hover:underline inline-flex items-center gap-1"
                            style={{ color: "var(--brand-sage-deep)" }}
                            target="_blank"
                            data-testid={`admin-open-report-${a.id}`}
                          >
                            Open report <ExternalLink className="w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "leads" && (
        <div className="glass rounded-2xl p-4 overflow-x-auto">
          <table className="evidence-table min-w-[900px]">
            <thead>
              <tr>
                <th>Created</th>
                <th>Email</th>
                <th>Name</th>
                <th>Title</th>
                <th>Property</th>
                <th>Score</th>
                <th>Priority</th>
                <th>HubSpot</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id}>
                  <td className="font-mono text-xs">
                    {new Date(l.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="text-xs">{l.email}</td>
                  <td className="text-xs">
                    {[l.first_name, l.last_name].filter(Boolean).join(" ") || "—"}
                  </td>
                  <td className="text-xs">{l.job_title || "—"}</td>
                  <td className="text-xs">{l.company || "—"}</td>
                  <td className="num">{l.knownfor_score}</td>
                  <td>
                    <span
                      className={`pill ${l.lead_priority === "High" ? "pill-gold" : "pill-neutral"}`}
                    >
                      {l.lead_priority}
                    </span>
                  </td>
                  <td className="text-xs">
                    {l.hubspot_contact_id ? (
                      <span className="text-[color:var(--score-high)]">✓ {l.hubspot_contact_id.slice(0, 8)}</span>
                    ) : (
                      <span className="text-[color:var(--score-low)]">✗ sync failed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function HealthStrip({ health, probing, onRefresh }) {
  const serper = health?.dependencies?.serper;
  const ok = serper?.ok === true;
  const border = ok ? "rgba(76, 155, 92, 0.35)" : "rgba(190, 90, 60, 0.4)";
  const iconColor = ok ? "var(--score-high)" : "var(--score-low)";

  return (
    <div
      className="glass rounded-2xl p-4 mb-6 flex items-center gap-4"
      style={{ borderColor: border }}
      data-testid="admin-health-strip"
    >
      <div style={{ color: iconColor }}>
        {ok ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-eyebrow mb-0.5">System health · Serper</div>
        <div className="text-sm" style={{ color: "var(--text-primary)" }} data-testid="admin-health-serper-status">
          {serper?.message || (probing ? "Checking Serper…" : "Not yet checked.")}
        </div>
        {serper?.cached && serper?.age_seconds != null && (
          <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
            Cached · checked {Math.round(serper.age_seconds)}s ago
          </div>
        )}
      </div>
      {!ok && (
        <a
          href="https://serper.dev/api-key"
          target="_blank"
          rel="noreferrer"
          className="btn-ghost text-xs !py-1.5 !px-3"
          data-testid="admin-health-topup-link"
        >
          Top up
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
      <button
        className="btn-ghost text-xs !py-1.5 !px-3"
        onClick={onRefresh}
        disabled={probing}
        data-testid="admin-health-refresh-btn"
      >
        {probing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        Re-probe
      </button>
    </div>
  );
}
